import type { OnlineStorePublishSummary } from "../shopify_publish/types";
import { atom } from "jotai";
import { DCDesignLicensing, DCDesignVersion, DCLicensingInterval, DCLicensingModel } from "../design_creator/types";

// #region ProductListingDesign
/** One catalog design as returned by GET /api/v2/catalog. */
export interface ProductListingDesign {
    id: number;
    title: string;
    pattern_type?: string | null;
    image_url?: string | null;
    preview_url?: string | null;
    /**
     * The design's current version — the print-ready file. Present because the
     * endpoint returns the raw `dc_designs` row; the card only needs the flattened
     * `image_url` above, but ordering a print needs the version itself.
     */
    current_version?: DCDesignVersion | null;
    /** 'crop' means the mural is printed cropped rather than scaled to fit. */
    infinity_mural_mode?: string | null;
    // Owning brand — the creator attribution shown on the card.
    brand?: { id: number; name: string } | null;
    creator?: { id: number; first?: string | null; last?: string | null } | null;
    licensing: DCDesignLicensing;
    // True when this brand already added the design to its storefront.
    in_use: boolean;
    // This brand's curation of the design. Absent for admin browse.
    is_favorite?: boolean;
    is_hidden?: boolean;
    sample_names?: string[];
    /** Buyer-facing images, ordered. Only returned by GET /catalog/designs/:id. */
    gallery?: CatalogGalleryImage[];
    /** True when a default wallpaper scale option exists for this design. */
    has_default_wallpaper_scale?: boolean;
    /** True when a sample exists that is selected and finished rendering. */
    has_selected_sample?: boolean;
    /**
     * False when the design has not finished the setup wizard, so it cannot be
     * ordered. Undefined on stale payloads — treat as true and let the server
     * (which hard-rejects) be the backstop.
     */
    is_setup_complete?: boolean;
    /**
     * True when the creator configured and published wrapping paper for this
     * design, so a licensee can optionally add a wrapping-paper product too.
     */
    has_wrapping_paper?: boolean;
}

// #region CatalogGalleryImage
export type CatalogGalleryImageType = 'listing' | 'mockup' | 'sample' | 'artwork';

export interface CatalogGalleryImage {
    url: string;
    thumbnail_url?: string | null;
    type: CatalogGalleryImageType;
    position: number;
    alt_text?: string | null;
}

export interface ProductListingPage {
    rows: ProductListingDesign[];
    total: number;
    page: number;
    limit: number;
}

export type ProductListingSort = 'newest' | 'title';

/**
 * Which curated slice of the catalog to browse. Hidden designs are suppressed
 * everywhere except the 'hidden' view — otherwise un-hiding one is impossible.
 */
export type CatalogView = 'all' | 'favorites' | 'hidden';

export interface ProductListingFilters {
    search?: string;
    brandIds?: number[];
    models?: DCLicensingModel[];
    sort?: ProductListingSort;
}

// #region BrandCatalogUsage
export type CatalogUsageStatus = 'ACTIVE' | 'AT_RISK' | 'PENDING_CANCEL' | 'INACTIVE';

/**
 * Whether this licensee's Shopify gallery carries the owner's full image set.
 * Licences added before the add-time media push existed show a single image
 * until the background sync re-uploads them.
 */
export type MediaSyncStatus = 'SYNCED' | 'NEEDS_RESYNC' | 'SYNCING';

/** Retries stop here; mirrors MAX_MEDIA_SYNC_ATTEMPTS on the server. */
export const MAX_MEDIA_SYNC_ATTEMPTS = 5;

/** One row of the brand's own licensed-design list (GET /api/v2/catalog/usage). */
export interface BrandCatalogUsage {
    id: number;
    design_id: number;
    design_title?: string | null;
    design_image_url?: string | null;
    design_preview_url?: string | null;
    creator_brand_name?: string | null;
    creator_first?: string | null;
    creator_last?: string | null;
    retail_pricing_template_id: number | null;
    licensing_model: DCLicensingModel;
    percentage_value: number | null;
    flat_amount: number | null;
    flat_interval: DCLicensingInterval | null;
    currency: string;
    subscription_id: string | null;
    auto_renew: boolean;
    status: CatalogUsageStatus;
    current_period_end: string | null;
    shopify_product_id: string | null;
    /** Licensee's wrapping-paper product; null when they sell wallpaper only. */
    giftwrap_shopify_product_id?: string | null;
    media_sync_status?: MediaSyncStatus;
    media_sync_attempts?: number;
    media_last_synced_at?: string | null;
}

export interface BrandCatalogUsagePage {
    rows: BrandCatalogUsage[];
    total: number;
    page: number;
    limit: number;
}

// #region AddToStorefrontResult
/** Never throws — the dialog branches on `status` (409 duplicate, 400, 402). */
export interface AddToStorefrontResult {
    ok: boolean;
    status: number;
    message?: string;
    shopify_product_id?: string | null;
    giftwrap_shopify_product_id?: string | null;
    /** Set when wrapping paper was requested but could not be added. */
    giftwrap_warning?: string;
    /** The new licence row id, for the Online Store publish retry. */
    usage_id?: number | null;
    /** Whether the new product reached the licensee's Online Store. */
    online_store_publish?: OnlineStorePublishSummary | null;
}

export const PRODUCT_LISTING_PAGE_SIZE = 24;
export const CATALOG_USAGE_PAGE_SIZE = 25;

export const productListingDesignsAtom = atom<ProductListingPage | null>(null);
export const productListingFiltersAtom = atom<ProductListingFilters>({ sort: 'newest' });
export const productListingSearchAtom = atom<string>('');
export const brandCatalogUsageAtom = atom<BrandCatalogUsagePage | null>(null);
export const productListingLoadingAtom = atom<boolean>(false);
